import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { CartContext, API } from '../App';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useContext(CartContext);
  const [loading, setLoading] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [orderData, setOrderData] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    city: 'Mumbai',
    pincode: ''
  });
  
  const [errors, setErrors] = useState({});
  
  const deliveryCharge = 999;
  const orderTotal = cartTotal + deliveryCharge;

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone)) newErrors.phone = 'Enter a valid 10-digit mobile number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Enter a valid 6-digit pincode';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const orderPayload = {
        ...formData,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          price: item.price,
          quantity: item.quantity,
          image_url: (item.image_urls && item.image_urls.length > 0) ? item.image_urls[0] : (item.image_url || "")
        })),
        subtotal: cartTotal,
        delivery_charge: deliveryCharge,
        total: orderTotal
      };
      
      const response = await fetch(`${API}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to place order');
      }
      
      const data = await response.json();
      setOrderData(data);
      setOrderConfirmed(true);
      clearCart();
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderConfirmed) {
    navigate('/cart');
    return null;
  }

  if (orderConfirmed && orderData) {
    return (
      <div className="min-h-screen bg-white" data-testid="checkout-page">
        <Navbar />
        <div className="pt-24 pb-32">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="py-16">
              <CheckCircle className="w-20 h-20 mx-auto mb-6 text-green-500" />
              <h1 className="font-heading text-3xl font-semibold text-[#1A1A1A] mb-4" data-testid="order-success-title">
                Order Confirmed!
              </h1>
              <p className="font-body text-lg text-[#666666] mb-2">Thank you for your order, {formData.customer_name}!</p>
              <p className="font-heading text-xl font-medium text-[#C4A882] mb-6" data-testid="order-id">
                Order ID: {orderData.order_id}
              </p>
              <p className="font-body text-[#666666] mb-8 max-w-md mx-auto">
                Our team will contact you within 24 hours to confirm your order and arrange delivery.
              </p>
              <div className="bg-[#F9F9F8] p-6 mb-8 text-left">
                <h3 className="font-heading text-lg font-medium mb-4">Delivery Address</h3>
                <p className="font-body text-[#666666]">
                  {formData.address}<br />
                  {formData.city} - {formData.pincode}
                </p>
              </div>
              <Link to="/products" className="btn-primary inline-flex items-center" data-testid="continue-shopping-btn">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-8 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/cart" className="inline-flex items-center text-sm text-[#666666] hover:text-[#1A1A1A] mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Cart
          </Link>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight">
            Checkout
          </h1>
        </div>
      </section>

      {/* Checkout Form */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h2 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-6">Contact Information</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name" className="font-body text-sm">Full Name *</Label>
                      <Input
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        className={`mt-1 rounded-none ${errors.customer_name ? 'border-red-500' : ''}`}
                        data-testid="input-name"
                      />
                      {errors.customer_name && <p className="text-red-500 text-xs mt-1">{errors.customer_name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-body text-sm">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="9876543210"
                        className={`mt-1 rounded-none ${errors.phone ? 'border-red-500' : ''}`}
                        data-testid="input-phone"
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="email" className="font-body text-sm">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`mt-1 rounded-none ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="input-email"
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-6">Delivery Address</h2>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="address" className="font-body text-sm">Street Address *</Label>
                      <Textarea
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows={3}
                        className={`mt-1 rounded-none resize-none ${errors.address ? 'border-red-500' : ''}`}
                        data-testid="input-address"
                      />
                      {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="font-body text-sm">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="mt-1 rounded-none"
                          data-testid="input-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pincode" className="font-body text-sm">Pincode *</Label>
                        <Input
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          placeholder="400070"
                          className={`mt-1 rounded-none ${errors.pincode ? 'border-red-500' : ''}`}
                          data-testid="input-pincode"
                        />
                        {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full btn-primary flex items-center justify-center disabled:opacity-50"
                    data-testid="place-order-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Place Order'
                    )}
                  </button>
                  <p className="font-body text-xs text-[#666666] text-center mt-4">
                    By placing this order, you agree to our terms and conditions. Payment will be collected upon delivery.
                  </p>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#F9F9F8] p-6 sticky top-24" data-testid="checkout-summary">
                <h2 className="font-heading text-xl font-semibold mb-6">Order Summary</h2>
                
                {/* Items */}
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-4">
                      <img 
                        src={(item.image_urls && item.image_urls.length > 0) ? item.image_urls[0] : (item.image_url || 'https://via.placeholder.com/400x300?text=in-dekor')} 
                        alt={item.name}
                        className="w-16 h-16 object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-body text-sm text-[#1A1A1A] line-clamp-1">{item.name}</p>
                        <p className="font-body text-xs text-[#666666]">Qty: {item.quantity}</p>
                        <p className="font-heading text-sm font-medium">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-[#E5E5E5] pt-4 space-y-3">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-[#666666]">Subtotal</span>
                    <span>₹{cartTotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-[#666666]">Delivery (Mumbai)</span>
                    <span>₹{deliveryCharge.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t border-[#E5E5E5] pt-3">
                    <div className="flex justify-between font-heading text-lg font-semibold">
                      <span>Total</span>
                      <span data-testid="checkout-total">₹{orderTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
